import {
  Button,
  ComboBox,
  ComboBoxItem,
  Field,
} from "@cvpartner/design-system";
import * as React from "react";
import { Form, FormValidationContext } from "react-aria-components";
import * as LibCountryAndOfficeIdSelector from "@/auto-converted/lib/CountryAndOfficeIdSelector.ts";
import { name_for_legacy as name_for_legacy_country_code } from "@/auto-converted/lib/CountryCodeUtils.ts";
import { Wrapper } from "@/login/shared/Modern.tsx";
import type { Country } from "@/schema/shared.ts";
import { useAllCountries } from "@/stores/Countries.ts";
import {
  useCurrentUser,
  useUpdateCountryAndOffice,
} from "@/stores/CurrentUser.tsx";
import { I18n } from "@/util/translations.ts";
import { CardContent } from "./shared/LoginCard.tsx";

const FormButton: React.FC<{ handleSave: () => void }> = ({ handleSave }) => {
  const validationErrors = React.use(FormValidationContext);

  return (
    <Button
      onPress={handleSave}
      disabled={Object.keys(validationErrors).length > 0}
    >
      {I18n.t("signin.continue_button")}
    </Button>
  );
};

const ShowComponent: React.FC<{
  countries: readonly Country[];
  country_id: string | null;
  office_id: string | null;
}> = ({ countries, country_id, office_id }) => {
  const [selected_country_id, set_selected_country_id] = React.useState(
    () => country_id || LibCountryAndOfficeIdSelector.firstCountryId(countries),
  );
  const [selected_office_id, set_selected_office_id] = React.useState(
    () => office_id || LibCountryAndOfficeIdSelector.firstOfficeId(countries),
  );

  const updateCountryAndOffice = useUpdateCountryAndOffice();

  React.useEffect(() => {
    set_selected_country_id((current) => {
      if (country_id) {
        return country_id;
      }

      return current;
    });
  }, [country_id]);

  React.useEffect(() => {
    set_selected_office_id((current) => {
      if (office_id) {
        return office_id;
      }

      return current;
    });
  }, [office_id]);

  const handle_save = () => {
    if (!selected_office_id || !selected_country_id) {
      return;
    }
    const update_hash = {
      country_id: selected_country_id,
      office_id: selected_office_id,
    };
    return updateCountryAndOffice(update_hash, "/home");
  };

  if (!selected_country_id) {
    return (
      <Wrapper
        heading={I18n.t("signin.header_title")}
        subheading={I18n.t("signin.header_subtitle_SSO")}
      >
        {null}
      </Wrapper>
    );
  }

  const officesForCountry =
    countries.find((country) => country.id === selected_country_id)?.offices ??
    [];

  return (
    <Wrapper
      heading={I18n.t("signin.header_title")}
      subheading={I18n.t("signin.header_subtitle_SSO")}
    >
      <Form
        validationErrors={
          officesForCountry.length > 0
            ? undefined
            : { department_picker: I18n.t("no_departments_added_to_country") }
        }
      >
        <CardContent>
          <Field label={I18n.t("signin.country_label")}>
            <ComboBox
              isRequired={true}
              onChange={(country_id) => {
                if (
                  typeof country_id !== "string" ||
                  country_id === selected_country_id
                ) {
                  return;
                }

                const firstOffice = countries.find(
                  (country) => country.id === country_id,
                )?.offices[0]?.id;

                set_selected_country_id(country_id);
                set_selected_office_id(firstOffice);
              }}
              value={selected_country_id}
              defaultItems={countries}
            >
              {(country) => (
                <ComboBoxItem>
                  {name_for_legacy_country_code(country.code)}
                </ComboBoxItem>
              )}
            </ComboBox>
          </Field>
          <Field label={I18n.t("signin.department_label")}>
            <ComboBox
              name="department_picker"
              isRequired={true}
              onChange={(office_id) => {
                if (typeof office_id === "string") {
                  set_selected_office_id(office_id);
                }
              }}
              value={selected_office_id || null}
              defaultItems={officesForCountry}
            >
              {(office) => <ComboBoxItem>{office.name!}</ComboBoxItem>}
            </ComboBox>
          </Field>
          <FormButton handleSave={handle_save} />
        </CardContent>
      </Form>
    </Wrapper>
  );
};

export const Show: React.FC = () => {
  const { data: countries } = useAllCountries();

  const { user } = useCurrentUser();

  if (countries == null || user == null) {
    return (
      <Wrapper
        heading={I18n.t("signin.header_title")}
        subheading={I18n.t("signin.header_subtitle_SSO")}
      />
    );
  }

  return (
    <ShowComponent
      countries={countries}
      country_id={user.country_id}
      office_id={user.office_id}
    />
  );
};
